/* DigestsTab — compose & send monthly digest emails to all active subscribers.

   Flow:
   1. Click "Draft from System Version" → backend pulls published trends & pre-fills the form
   2. Edit subject / intro / body / per-tier addendums
   3. Click "Preview" → see rendered HTML in iframe + recipient count
   4. Click "Send to N subscribers" → emails go out, persisted to history

   History sidebar lists past digests with status pills.
*/
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Send, Eye, Trash2, RefreshCcw, CheckCircle2, AlertCircle } from "lucide-react";
import { studioHeaders } from "@/lib/studioAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_LABEL = {
    "continuity-68": "Lite",
    "continuity-168": "Standard",
    "continuity-348": "Pro",
};

const STATUS_STYLES = {
    draft: "bg-cafe-cream border-border text-cafe-mocha",
    sending: "bg-blue-50 border-blue-300 text-blue-700",
    sent: "bg-cafe-olive/15 border-cafe-olive/40 text-cafe-olive",
    sent_with_errors: "bg-amber-50 border-amber-300 text-amber-800",
};

export default function DigestsTab() {
    const [history, setHistory] = useState([]);
    const [draft, setDraft] = useState(null);
    const [previewHtml, setPreviewHtml] = useState("");
    const [previewMeta, setPreviewMeta] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/digests`, { headers: studioHeaders() });
            setHistory(r.data || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load digests");
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const newFromSystem = async () => {
        try {
            setBusy(true);
            const r = await axios.post(`${API}/digests/draft-from-system`, {}, { headers: studioHeaders() });
            const { _preview_trends, _preview_published_version, ...d } = r.data;
            setDraft(d);
            toast.success(`Drafted from v${_preview_published_version || d.month} (${(_preview_trends || []).length} trends)`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Draft failed — publish a System Version first");
        } finally {
            setBusy(false);
        }
    };

    const openExisting = (d) => setDraft({ ...d });

    const saveDraft = async () => {
        if (!draft) return;
        try {
            setBusy(true);
            const payload = {
                month: draft.month, subject: draft.subject || "", intro: draft.intro || "",
                body_html: draft.body_html || "", target_tiers: draft.target_tiers || [],
                tier_addendums: draft.tier_addendums || {},
                include_trend_cards: draft.include_trend_cards !== false,
            };
            const r = await axios.post(`${API}/digests`, payload, { headers: studioHeaders() });
            setDraft({ ...draft, id: r.data.id });
            toast.success("Draft saved");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setBusy(false); }
    };

    const preview = async () => {
        if (!draft?.id) { toast.error("Save the draft first"); return; }
        try {
            setBusy(true);
            const r = await axios.post(
                `${API}/digests/send`,
                { digest_id: draft.id, dry_run: true },
                { headers: studioHeaders() },
            );
            setPreviewHtml(r.data.preview_html_first_recipient || "");
            setPreviewMeta(r.data);
            if (r.data.recipient_count === 0) {
                toast.info("0 active subscribers match — nothing to send yet");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Preview failed");
        } finally { setBusy(false); }
    };

    const sendNow = async () => {
        if (!draft?.id || !previewMeta) { toast.error("Preview first to confirm recipient count"); return; }
        if (!window.confirm(`Send this digest to ${previewMeta.recipient_count} subscriber(s)? This cannot be undone.`)) return;
        try {
            setBusy(true);
            toast.info("Sending — this takes a few seconds per recipient…");
            const r = await axios.post(
                `${API}/digests/send`,
                { digest_id: draft.id, dry_run: false },
                { headers: studioHeaders(), timeout: 120000 },
            );
            toast.success(`Sent to ${r.data.sent} subscribers${r.data.failed?.length ? `, ${r.data.failed.length} failed` : ""}`);
            setDraft(null);
            setPreviewHtml("");
            setPreviewMeta(null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Send failed");
        } finally { setBusy(false); }
    };

    const remove = async (id) => {
        if (!window.confirm(`Delete the draft "${(history.find((h) => h.id === id)?.month) || id}"?`)) return;
        try {
            await axios.delete(`${API}/digests/${id}`, { headers: studioHeaders() });
            if (draft?.id === id) setDraft(null);
            await load();
            toast.success("Deleted");
        } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6" data-testid="digests-tab">
            {/* Left: history rail */}
            <aside className="border-r border-border pr-5 space-y-2">
                <button
                    onClick={newFromSystem}
                    disabled={busy}
                    className="w-full bg-cafe-terracotta hover:bg-cafe-terracotta/90 text-cafe-cream rounded-full px-4 py-2.5 text-xs font-bold uppercase tracking-[0.12em] flex items-center justify-center gap-2 mb-3"
                    data-testid="new-digest-btn"
                >
                    <Sparkles className="h-4 w-4" /> Draft from System
                </button>
                <p className="text-[10px] uppercase tracking-[0.22em] text-cafe-mocha font-bold mt-4 mb-1">History</p>
                {history.length === 0 && <p className="text-xs text-cafe-mocha italic">No digests yet.</p>}
                {history.map((d, i) => (
                    <button
                        key={d.id || `digest-${i}`}
                        onClick={() => openExisting(d)}
                        className={`w-full text-left px-3 py-2.5 rounded-lg border ${draft?.id === d.id ? "border-cafe-olive bg-cafe-olive/10" : "border-border hover:bg-cafe-cream"}`}
                        data-testid={`digest-row-${d.month}`}
                    >
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-bold text-cafe-espresso">{d.month}</span>
                            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${STATUS_STYLES[d.status] || STATUS_STYLES.draft}`}>
                                {d.status}
                            </span>
                        </div>
                        <p className="text-xs text-cafe-mocha truncate mt-0.5">{d.subject || "(no subject)"}</p>
                        {d.status?.startsWith("sent") && (
                            <p className="text-[10px] text-cafe-mocha mt-0.5">{d.sent_count}/{d.recipient_count} delivered</p>
                        )}
                    </button>
                ))}
            </aside>

            {/* Right: editor + preview */}
            {!draft ? (
                <div className="text-center py-16 text-cafe-mocha italic">
                    Click &ldquo;Draft from System&rdquo; to start this month&apos;s digest, or open a past one on the left.
                </div>
            ) : (
                <div className="space-y-5">
                    <div className="flex items-center justify-between gap-3 border-b border-border pb-3">
                        <div>
                            <p className="text-[10px] uppercase tracking-[0.22em] text-cafe-olive font-bold">Digest editor</p>
                            <h2 className="font-serif text-2xl">{draft.month}</h2>
                            {draft.status?.startsWith("sent") && (
                                <p className="text-xs text-cafe-mocha mt-1 flex items-center gap-1.5">
                                    <CheckCircle2 className="h-3.5 w-3.5 text-cafe-olive" /> Sent to {draft.sent_count} subscribers
                                </p>
                            )}
                        </div>
                        <div className="flex gap-2">
                            {draft.status !== "sent" && draft.status !== "sent_with_errors" && (
                                <>
                                    <button onClick={saveDraft} disabled={busy} className="bg-cafe-cream border border-border hover:border-cafe-olive text-cafe-espresso rounded-full px-4 py-2 text-xs font-bold uppercase tracking-[0.12em] flex items-center gap-2" data-testid="save-digest">
                                        <RefreshCcw className="h-3.5 w-3.5" /> Save
                                    </button>
                                    <button onClick={preview} disabled={busy || !draft.id} className="bg-cafe-espresso hover:bg-cafe-mocha text-cafe-cream rounded-full px-4 py-2 text-xs font-bold uppercase tracking-[0.12em] flex items-center gap-2" data-testid="preview-digest">
                                        <Eye className="h-3.5 w-3.5" /> Preview
                                    </button>
                                    <button onClick={sendNow} disabled={busy || !previewMeta} className="bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream rounded-full px-4 py-2 text-xs font-bold uppercase tracking-[0.12em] flex items-center gap-2 disabled:opacity-40" data-testid="send-digest">
                                        <Send className="h-3.5 w-3.5" /> Send{previewMeta ? ` to ${previewMeta.recipient_count}` : ""}
                                    </button>
                                    {draft.id && (
                                        <button onClick={() => remove(draft.id)} className="text-cafe-terracotta hover:bg-cafe-terracotta/10 rounded-full p-2" title="Delete">
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    {/* Form */}
                    <div className="space-y-4">
                        <Field label="Subject line">
                            <input
                                type="text"
                                value={draft.subject || ""}
                                onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                                className="w-full border border-border rounded-lg px-3 py-2.5 text-sm bg-white"
                                data-testid="digest-subject"
                            />
                        </Field>
                        <Field label="Intro (italic paragraph above body)">
                            <textarea
                                rows={2}
                                value={draft.intro || ""}
                                onChange={(e) => setDraft({ ...draft, intro: e.target.value })}
                                className="w-full border border-border rounded-lg px-3 py-2.5 text-sm bg-white"
                                data-testid="digest-intro"
                            />
                        </Field>
                        <Field label="Body HTML (your main message — appears above the trend cards)">
                            <textarea
                                rows={4}
                                value={draft.body_html || ""}
                                onChange={(e) => setDraft({ ...draft, body_html: e.target.value })}
                                placeholder="<p>Write your monthly note here in HTML...</p>"
                                className="w-full border border-border rounded-lg px-3 py-2.5 text-sm bg-white font-mono"
                                data-testid="digest-body"
                            />
                        </Field>

                        <Field label="Target tiers (who receives this)">
                            <div className="flex gap-2 flex-wrap">
                                {["continuity-68", "continuity-168", "continuity-348"].map((t) => {
                                    const on = (draft.target_tiers || []).includes(t);
                                    return (
                                        <button
                                            key={t}
                                            onClick={() => {
                                                const next = on ? (draft.target_tiers || []).filter((x) => x !== t) : [...(draft.target_tiers || []), t];
                                                setDraft({ ...draft, target_tiers: next });
                                            }}
                                            className={`px-3 py-1.5 rounded-full border-2 text-xs font-bold uppercase tracking-wider ${on ? "border-cafe-olive bg-cafe-olive text-cafe-cream" : "border-border text-cafe-mocha"}`}
                                            data-testid={`tier-toggle-${t}`}
                                        >
                                            {TIER_LABEL[t]}
                                        </button>
                                    );
                                })}
                            </div>
                        </Field>

                        <Field label="Per-tier addendums (extra section shown only to that tier)">
                            <div className="space-y-2">
                                {["continuity-168", "continuity-348"].map((t) => (
                                    <div key={t}>
                                        <p className="text-[10px] uppercase tracking-wider text-cafe-mocha font-bold mb-1">{TIER_LABEL[t]} addendum</p>
                                        <textarea
                                            rows={2}
                                            value={(draft.tier_addendums || {})[t] || ""}
                                            onChange={(e) => setDraft({
                                                ...draft,
                                                tier_addendums: { ...(draft.tier_addendums || {}), [t]: e.target.value }
                                            })}
                                            className="w-full border border-border rounded-lg px-3 py-2 text-xs bg-white font-mono"
                                            placeholder={`<p>Extra content for ${TIER_LABEL[t]} subscribers only...</p>`}
                                            data-testid={`addendum-${t}`}
                                        />
                                    </div>
                                ))}
                            </div>
                        </Field>

                        <label className="flex items-center gap-2 text-xs text-cafe-mocha">
                            <input
                                type="checkbox"
                                checked={draft.include_trend_cards !== false}
                                onChange={(e) => setDraft({ ...draft, include_trend_cards: e.target.checked })}
                                data-testid="include-trends"
                            />
                            Include trend cards from current published System Version
                        </label>
                    </div>

                    {/* Preview iframe */}
                    {previewMeta && (
                        <div className="border border-border rounded-2xl p-5 bg-white" data-testid="digest-preview">
                            <div className="flex items-center justify-between mb-3 pb-3 border-b border-border">
                                <p className="text-[10px] uppercase tracking-[0.22em] text-cafe-olive font-bold">Preview · {previewMeta.recipient_count} subscriber(s) will receive this</p>
                                <div className="flex gap-2 text-[10px]">
                                    {Object.entries(previewMeta.tiers_breakdown || {}).map(([tier, count]) => (
                                        <span key={tier} className="px-2 py-0.5 bg-cafe-cream rounded-full text-cafe-mocha">{TIER_LABEL[tier]}: {count}</span>
                                    ))}
                                </div>
                            </div>
                            {previewHtml ? (
                                <iframe
                                    title="Digest preview"
                                    srcDoc={previewHtml}
                                    className="w-full h-[600px] border border-border rounded-lg bg-cafe-cream"
                                />
                            ) : (
                                <p className="text-sm text-cafe-mocha italic flex items-center gap-2"><AlertCircle className="h-4 w-4" /> No subscribers match this target — preview unavailable. Save and click Send once you have subscribers.</p>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-cafe-mocha font-bold mb-1.5">{label}</p>
            {children}
        </div>
    );
}
